import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getPayrocSessionToken } from 'api/Transaction';

import { GENERIC_ERROR } from 'lib/constants/messages';

import { getAchStyles, getCardStyles } from '../lib/styles';
import { SESSION_ERROR, buildErrorHandler, enforceCvvWhenAccepted } from '../lib/errors';
import { achFields, cardFields, getAchWrapperMap, getCardWrapperMap } from '../lib/fields';
import { getInvalidRequiredFields, markFieldError, syncFieldError } from '../lib/fieldValidity';

const PAYROC_HOSTED_FIELDS_SCRIPT = process.env.REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT;
const NO_TERMINAL_CODE = 6;

export const usePayroc = ({
  type,
  containerRef,
  amount,
  handlePayment,
  validate,
  id,
  mode = 'payment',
  buttonText,
  secureTokenId,
  terminalPin,
  getSessionToken,
  isFullWidthButton = false,
  onError = () => {}
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [scriptAttempt, setScriptAttempt] = useState(0);
  // State lags a retry by a render, so the guard against double-initialising reads a ref.
  const formInstanceRef = useRef(null);

  const isCard = type === 'card';
  const isAch = type === 'ach';

  // Keeps every hosted field bound to this instance's own DOM nodes — two live
  // instances share the same class names and would otherwise fight over them.
  const scope = id ? `#${CSS.escape(id)} ` : '';
  // The submit button often renders outside the scoped container (modal footers),
  // so it is matched by a per-instance class instead of the scope prefix.
  const submitTarget = id ? `.payroc-submit-${CSS.escape(id)}` : undefined;

  const requiredFields = isCard
    ? ['cardholderName', 'cardNumber', 'cvv', 'expiryDate']
    : ['nameOnAccount', 'accountType', 'achAccountNumber', 'routingNumber'];

  const fieldValidityRef = useRef({});
  const handlePaymentRef = useRef(handlePayment);
  const validateRef = useRef(validate);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    handlePaymentRef.current = handlePayment;
  }, [handlePayment]);

  useEffect(() => {
    validateRef.current = validate;
  }, [validate]);

  const sessionParamsRef = useRef({ mode, secureTokenId, terminalPin, getSessionToken });
  sessionParamsRef.current = { mode, secureTokenId, terminalPin, getSessionToken };

  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement('script');

    try {
      script.src = PAYROC_HOSTED_FIELDS_SCRIPT;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () =>
        setError({ error: "Couldn't load the payment form. Please try again." });
      document.body.appendChild(script);
    } catch (err) {
      console.error('Error loading 3rd-party script:', err);
      setError({ error: "Couldn't load the payment form. Please try again." });
    }

    return () => {
      script.remove();
    };
  }, [id, scriptAttempt]);

  const initializeForm = useCallback(
    async (formFields, wrapperMap) => {
      const Payroc = window.Payroc;
      const fieldConfig = formFields?.fields?.card || formFields?.fields?.ach || {};

      // Requesting a session token before this guard burns a Payroc session on
      // every unmounted / not-yet-loaded init.
      if (!containerRef.current || !Payroc?.hostedFields) return;

      try {
        setIsLoading(true);
        const sessionParams = sessionParamsRef.current;
        const res = await (sessionParams.getSessionToken?.(sessionParams) ??
          getPayrocSessionToken(navigate, {
            scenario: sessionParams.mode,
            secureTokenId: sessionParams.secureTokenId,
            terminalPin: sessionParams.terminalPin
          }));
        const sessionToken = res?.token;

        if (!sessionToken) {
          console.warn('Payment session token unavailable', {
            error: res?.error,
            detail: res?.detail
          });
          setError({ error: res?.code === NO_TERMINAL_CODE ? res.error : SESSION_ERROR });
          return;
        }

        if (containerRef.current && Payroc.hostedFields && sessionToken) {
          const instance = new Payroc.hostedFields({
            ...formFields,
            sessionToken,
            onPreSubmit: async () => {
              setSubmitError(null);
              // Re-applied here, not after initialize(): the card-number field posts its
              // terminal cvv setting back once it validates, which reverts the promotion.
              if (isCard) enforceCvvWhenAccepted(formInstanceRef.current);
              const invalidRequired = getInvalidRequiredFields(
                requiredFields,
                fieldValidityRef.current
              );
              if (invalidRequired.length) {
                invalidRequired.forEach((f) => markFieldError(f, wrapperMap, fieldConfig));
                return false;
              }
              if (validateRef.current) {
                const isValid = await validateRef.current();
                return isValid;
              }
              return true;
            }
          });

          instance.on(
            'error',
            buildErrorHandler({
              onSubmissionError: (message) => {
                setSubmitError(message);
                onErrorRef.current?.(message);
              },
              onInitError: (message, meta) => setError({ error: message, ...meta })
            })
          );

          await instance.initialize();

          instance.on('submissionSuccess', async (data) => {
            if (!handlePaymentRef.current) {
              setError({
                error: "This payment method isn't supported",
                type: 'error',
                field: 'payment_method'
              });
              return;
            }

            const result = await handlePaymentRef.current(data);
            if (result?.code !== 0) {
              setError(result);
              return;
            }
          });

          instance.on('fieldValidityChange', ({ field, validationError }) => {
            fieldValidityRef.current[field] = !validationError;
            syncFieldError(field, wrapperMap, fieldConfig, validationError);
          });

          return instance;
        }
      } catch (err) {
        const rawMessage = err?.error || err?.message || '';
        const isConnectFailure =
          typeof rawMessage === 'string' && rawMessage.includes('to connect');
        if (isConnectFailure) {
          console.warn('Payroc hosted fields failed to connect', { message: rawMessage });
        }
        const message = isConnectFailure
          ? "Couldn't load the card form. Please try again."
          : rawMessage || GENERIC_ERROR;
        setError({ error: message });
      } finally {
        setIsLoading(false);
      }
    },
    [containerRef]
  );

  const initCardForm = useCallback(async () => {
    if (formInstanceRef.current || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { card: cardFields({ amount, buttonText, scope, submitTarget }) },
      styles: getCardStyles({ isFullWidthButton })
    };

    const hostedFieldsFormCard = await initializeForm(formFields, getCardWrapperMap(scope));
    formInstanceRef.current = hostedFieldsFormCard ?? null;
  }, [initializeForm, amount, scriptLoaded, buttonText, scope, submitTarget, isFullWidthButton]);

  const initACHForm = useCallback(async () => {
    if (formInstanceRef.current || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { ach: achFields({ amount, buttonText, scope, submitTarget }) },
      styles: getAchStyles()
    };

    const hostedFieldsFormACH = await initializeForm(formFields, getAchWrapperMap(scope));
    formInstanceRef.current = hostedFieldsFormACH ?? null;
  }, [initializeForm, amount, scriptLoaded, buttonText, scope, submitTarget]);

  const startInitialization = useCallback(() => {
    if (isAch) {
      initACHForm();
    } else if (isCard) {
      initCardForm();
    }
  }, [initACHForm, initCardForm, isCard, isAch]);

  useEffect(() => {
    startInitialization();
  }, [startInitialization, retryCount]);

  const retry = useCallback(() => {
    // The error screen unmounts the containers, destroying the iframes. Dropping the cached
    // instance lets the re-init run at all, and bumping retryCount defers it to an effect —
    // initialising here would read a containerRef that the error screen has left null.
    formInstanceRef.current = null;
    fieldValidityRef.current = {};
    setError(null);
    setSubmitError(null);
    // A script that never loaded leaves the init guard closed, so the tag is re-appended.
    if (!scriptLoaded) setScriptAttempt((attempt) => attempt + 1);
    setRetryCount((count) => count + 1);
  }, [scriptLoaded]);

  return {
    isLoading,
    error,
    submitError,
    retry
  };
};
